/**
 * Shared helper to format the section title used in the exam header across
 * Reading, Listening, and Writing modules.
 *
 * Produces output identical to the previous per-module implementations so
 * English-locale rendering is byte-identical (e.g. "Goethe-Zertifikat A2: Writing").
 *
 * @module simulationSectionTitle
 */

import type { CEFRLevel, ExamType } from '@/types/exam-simulation';

/**
 * Human-readable brand name for a supported exam type.
 * Falls back to a Capitalized version of the raw string for unknown values so
 * we never crash on new exam types.
 */
export function getExamTypeName(type: ExamType | string): string {
  switch (type) {
    case 'goethe':
      return 'Goethe-Zertifikat';
    case 'osd':
      return 'ÖSD Zertifikat';
    case 'telc':
      return 'telc Deutsch';
    default: {
      const raw = type as string;
      if (!raw) return '';
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    }
  }
}

/**
 * Format the canonical simulation section title shown in `ExamHeader`.
 *
 * Example: `formatSimulationSectionTitle('goethe', 'A2', 'Writing')`
 *   → `"Goethe-Zertifikat A2: Writing"`.
 */
export function formatSimulationSectionTitle(
  examType: ExamType | string,
  level: CEFRLevel | string,
  moduleLabel: string,
): string {
  return `${getExamTypeName(examType)} ${level}: ${moduleLabel}`;
}
